import { ParsedUrlQuery } from 'querystring';

import { paramsOutsideAlias, paramsOutsideFilters } from 'consts/filters';
import { SortId, sortIds } from 'consts/sort';
import { Filter } from 'types/Filter';
import omit from 'utils/omit';
import { IntlShape } from 'utils/intl';

import messages from './SearchController.messages';

export type FilterValue = {
  id: string;
  searchKey: string;
  name: string;
  count: number;
  min?: number;
  max?: number;
};

export type MappedFilter = {
  key: string;
  name: string;
  values: FilterValue[];
  isRange: boolean;
  nodes?: MappedFilter[];
};

export type FilterTag = {
  searchKey: string;
  values: string[];
  label: string;
};

export const getNestedFilters = (
  filters: Filter[],
  aliasQueryKeys: string[],
  reference: number | null,
  isMainLevel?: boolean,
  isAdvanced?: boolean
): MappedFilter[] | undefined =>
  reference
    ? filters
        .filter((filter) => !aliasQueryKeys.includes(filter.key))
        .filter((filter) =>
          isMainLevel ? [reference, null].includes(filter.product_type_id) : filter.product_type_id === reference
        )
        .filter((filter) => !isMainLevel || isAdvanced === filter.is_advanced)
        .flatMap((filter) => ({
          ...filter,
          isRange: filter.is_range,
          values: filter.values.map((value) => ({
            ...value,
            count: 'count' in value ? value.count : 1,
            name: 'name' in value ? value.name : '',
            id: 'name' in value ? value.name : '',
            searchKey: filter.key,
          })),
          nodes: getNestedFilters(filters, aliasQueryKeys, filter.references),
        }))
    : undefined;

export const getMappedFilters = (
  filters: Filter[] = [],
  aliasQueryKeys: string[] = [],
  mainProductTypeId: number | null = null,
  isAdvanced = false
): MappedFilter[] => getNestedFilters(filters, aliasQueryKeys, mainProductTypeId, true, isAdvanced) || [];

export const getFilterTags = (intl: IntlShape, query: ParsedUrlQuery, filters: Filter[] = []): FilterTag[] => {
  const filterParams = omit(query, [...paramsOutsideAlias, 'alias', 'product_type', 'gclid', 'is_available']);
  return Object.keys(filterParams)
    .map((key) => {
      const values = filterParams[key];
      const isQueryField = key === 'name';
      const filter = filters.find(({ key: filterKey }) =>
        [filterKey, `${filterKey}_from`, `${filterKey}_to`].includes(key)
      );
      const filterKey = filter?.key;
      const filterName = !isQueryField ? filter?.name : intl.formatMessage(messages.phrase);
      const suffixMap = {
        [`${filterKey}_from`]: intl.formatMessage(messages.from),
        [`${filterKey}_to`]: intl.formatMessage(messages.to),
      };
      const rangeFieldSuffix = filterKey !== key && !isQueryField ? ` (${suffixMap[key]})` : '';
      return {
        searchKey: key,
        values: Array.isArray(values) ? values : [values || ''],
        label: `${filterName}${rangeFieldSuffix}:` || '',
      };
    })
    .flat();
};

export const getCheckedFiltersCount = (query: ParsedUrlQuery): number =>
  Object.keys(omit(query, [...paramsOutsideFilters, 'product_type', 'gclid', 'is_available'])).length;

export const getCommonQueryParams = (
  query: ParsedUrlQuery
): {
  page: number;
  limit: number;
  currentSort: SortId;
  isAvailable: boolean;
} => {
  const page = Number(query.page) || 1;
  const limit = Number(query.limit) || 20;
  const currentSort = (
    sortIds.includes(Number(query.sort_id) as SortId) ? Number(query.sort_id) : SortId.Popularity
  ) as SortId;
  const isAvailable = (query.is_available as string) === '1';

  return { page, limit, currentSort, isAvailable };
};
